/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import axios from 'axios';
import urlJoin from 'proper-url-join';

import { PHONE_CHANNEL_TYPE } from '@ibm-watson/shared/modules/channels/util/channelTypes';
import { extractErrorMessage } from '@ibm-watson/shared/modules/ia/util/rest';
import { endpoints } from '../../../ia/util/config';

const endpointIntegrations = (assistantId: string) =>
  urlJoin(endpoints.store, '/v2/assistants/', assistantId, '/integrations');

const endpointIntegration = (assistantId: string, integrationId: string) =>
  urlJoin(
    endpoints.store,
    '/v2/assistants/',
    assistantId,
    '/integrations/',
    integrationId
  );

export const listIntegrations = (assistantId: string): Promise<any> =>
  axios
    .get(endpointIntegrations(assistantId), { params: { verbose: true } })
    .then(response => response.data);

export const getIntegration = (
  assistantId: string,
  integrationId: string
): Promise<any> =>
  axios
    .get(endpointIntegration(assistantId, integrationId), {
      params: {
        verbose: true
      }
    })
    .then(response => response.data);

export const createIntegration = (
  assistantId: string,
  integration: any
): Promise<any> =>
  axios
    .post(endpointIntegrations(assistantId), integration, {
      params: {
        verbose: true
      }
    })
    .then(response => response.data);

export const updateIntegration = (
  assistantId: string,
  integrationId: string,
  integration: any
): Promise<any> =>
  axios
    .post(endpointIntegration(assistantId, integrationId), integration, {
      params: {
        verbose: true
      }
    })
    .then(response => response.data)
    .catch(err => {
      if (
        integration.type === PHONE_CHANNEL_TYPE &&
        err?.response?.data?.error
      ) {
        const errorMessage = extractErrorMessage(err.response.data.error);
        if (errorMessage.startsWith('Unique Violation:')) {
          throw err.response.data.error;
        } else {
          throw err;
        }
      } else {
        throw err;
      }
    });

export const deleteIntegration = (
  assistantId: string,
  integrationId: string
): Promise<any> =>
  axios
    .delete(endpointIntegration(assistantId, integrationId), {
      params: {
        verbose: true
      }
    })
    .then(response => response.data);
